import { useState } from "react";
import axios from "axios";
import { useApiConfigStore } from "@store/api_config";
import { getVehicleImageUrl } from "@utils/vehicleImage";

const BRANDS: Record<string, string[]> = {
  Peugeot: [
    "208", "2008", "308", "3008", "5008",
    "508", "Partner", "Rifter", "e-208", "e-2008",
  ],
  Citroën: [
    "C3", "C4", "C5 Aircross", "C5 X", "Berlingo",
    "C3 Aircross", "e-C3", "e-C4", "e-Berlingo",
  ],
  Opel: [
    "Corsa", "Mokka", "Astra", "Grandland",
    "Crossland", "Combo", "Zafira",
  ],
};

const COLORS = [
  { value: "noir", label: "Noir" },
  { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" },
  { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" },
];

export default function CatalogScreen() {
  const getApiUrl = useApiConfigStore((s) => s.getApiUrl);
  const [selectedColor, setSelectedColor] = useState("noir");
  const [uploading, setUploading] = useState<string | null>(null);
  const [refreshKeys, setRefreshKeys] = useState<Record<string, number>>({});

  const handleUploadPhoto = async (rawBrand: string, rawModel: string, rawColor: string, file: File) => {
    const key = `${rawBrand}-${rawModel}-${rawColor}`;
    setUploading(key);
    try {
      const brand = rawBrand.toLowerCase().trim();
      const model = rawModel.toLowerCase().trim().replace(/\s+/g, "-");
      const color = rawColor.toLowerCase().trim();
      const formData = new FormData();
      formData.append("image", file);
      formData.append("brand", brand);
      formData.append("model", model);
      formData.append("color", color);
      await axios.post(`${getApiUrl()}/watermark/upload`, formData);
      setRefreshKeys((prev) => ({ ...prev, [key]: Date.now() }));
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="p-8 overflow-y-auto h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Catalogue véhicules</h1>
        <div className="flex gap-2">
          {COLORS.map((c) => (
            <button
              key={c.value}
              onClick={() => setSelectedColor(c.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedColor === c.value
                  ? "bg-[#5C6BFF] text-white"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {Object.entries(BRANDS).map(([brand, models]) => (
        <div key={brand} className="mb-8">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">{brand}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {models.map((model) => {
              const url = getVehicleImageUrl(brand, model, selectedColor);
              const key = `${brand}-${model}-${selectedColor}`;
              const isUploading = uploading === key;
              const cacheBust = refreshKeys[key] ? `?t=${refreshKeys[key]}` : "";
              return (
                <div
                  key={model}
                  className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col items-center group relative"
                >
                  <div className="h-[120px] w-full flex items-center justify-center mb-3">
                    {url ? (
                      <img
                        src={`${url}${cacheBust}`}
                        alt={`${brand} ${model}`}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    <span className="hidden text-gray-300 text-sm">Image indisponible</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{model}</span>
                  <label
                    className={`absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 text-xs text-white rounded-lg cursor-pointer ${
                      isUploading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-[#5C6BFF] hover:bg-[#4a59e6]"
                    }`}
                  >
                    {isUploading ? "..." : "Upload"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadPhoto(brand, model, selectedColor, f);
                      }}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
